/**
 * The organization the signed-in principal belongs to.
 *
 * `GET /me` is rendering data. The server authorizes nothing from it: every
 * route resolves the organization again from `org_members`, so a client that
 * shows a product on a stale answer still gets 403 from the data it asks for.
 * See section 7 of `docs/bastion_authorization.md`.
 *
 * A caller gates UI on `is_parametric === false` and leaves the UI open while
 * the query loads or fails. Hiding by default flashes the shell for every
 * internal user on every load.
 */

import { useQuery } from '@tanstack/react-query';
import type { paths } from '@bastion/api';
import { authenticatedFetch } from '../authenticatedFetch';

/**
 * The wire type comes from the server's OpenAPI schema, so a route change
 * fails the type check here. Regenerate it with
 * `npm run --workspace=@bastion/api generate-types`.
 */
export type Me = paths['/me']['get']['responses']['200']['content']['application/json'];

const ME_QUERY_KEY = ['me'] as const;

/** The membership changes rarely, and the server never trusts this answer. */
const ME_STALE_TIME_MS = 5 * 60 * 1000;

/**
 * Read the caller's organization.
 *
 * The key carries `userId` because the cache outlives a sign-out: the query
 * client is a module singleton, so a second sign-in in the same tab would
 * otherwise read the previous user's answer while it is still fresh, and an
 * internal user would get the trimmed shell. Pass the id from the auth store.
 * Without one the query stays disabled and `data` is undefined, which every
 * caller reads as "leave the UI open".
 *
 * The webapp calls this through `useOrgRendering`
 * (`bastion/webapp/src/app/useOrgRendering.ts`), which supplies the id. Import
 * that hook there instead of this one: a direct call that forgets the id
 * disables the query, and the shell then renders every product with no test to
 * catch it.
 */
export function useMe(userId: string | null | undefined) {
  return useQuery({
    queryKey: [...ME_QUERY_KEY, userId],
    queryFn: ({ signal }) => authenticatedFetch<Me>('/me', { signal }),
    enabled: Boolean(userId),
    staleTime: ME_STALE_TIME_MS,
  });
}
